import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { AssetDto, AssetsControllerService } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { toOptionItemModel } from '@shared/components/multi-select-with-exclude/multi-select-with-exclude.utils';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { keyBy } from 'lodash';
import { pipe, switchMap } from 'rxjs';

export const useAssets = () =>
  signalStoreFeature(
    {
      props: {
        _notificationService: type<NotificationService>(),
      },
    },
    withState({
      assets: new Array<AssetDto>(),
      assetsLoaded: false,
    }),
    withProps(() => ({
      _assetsService: inject(AssetsControllerService),
    })),
    withComputed(store => {
      const assetsOptions = computed(() => store.assets().map(lp => toOptionItemModel(lp, 'id', 'code')));
      const assetsMap = computed(() => keyBy(store.assets(), 'id'));
      const fiatCurrencies = computed(() => store.assets().filter(asset => asset.fiat));

      return {
        assetsOptions,
        assetsMap,
        fiatCurrencies,
      };
    }),
    withMethods(store => {
      const loadAssets = rxMethod<void>(
        pipe(
          switchMap(() =>
            store._assetsService.getAssets().pipe(
              tapResponse({
                next: (assets: AssetDto[]) => patchState(store, { assets, assetsLoaded: true }),
                error: (error: HttpErrorResponse) => {
                  store._notificationService.showErrorNotification('Failed to load assets', error);
                  patchState(store, { assetsLoaded: true });
                },
              }),
            ),
          ),
        ),
      );
      return {
        loadAssets,
      };
    }),
  );
